import { OrganizationTypes } from "shared/constants/OrganizationTypes";
import { ToOrganizationList } from "./OrganizationHelpers";

// "Decompose Groups into Parent Companies" support for the header organization selector.
//
// Offered to GCT users only. A GCT user's organization list is made up entirely of Groups, and
// decomposing one means narrowing it to a subset of the parent companies its member pharmacies
// (MMIDs) belong to.
//
// The organization selection itself stays single-select and stays a Group. The chosen parent
// companies are a refinement held separately as selectedRpidsForGroup on the UserContext, so
// nothing that scopes its data from currentOrganization is affected by the option being on.
//
// The parent companies come from the server, nested on each Group in the GetUserDropDownValues
// response and normalized onto the Group option by formatOrganizationDropdownOptions in Layout.js.

// Separates the originating Group's value from the parent company's RPID inside a child row's
// key. Chosen because organization ItemIds never contain it.
const DECOMPOSED_VALUE_SEPARATOR = "::";

function IsGroup(organization) {
  return (
    organization !== null &&
    organization !== undefined &&
    organization.type === OrganizationTypes.Group
  );
}

function hasValue(organization) {
  return (
    organization !== null &&
    organization !== undefined &&
    organization.value !== null &&
    organization.value !== undefined &&
    organization.value !== ""
  );
}

/**
 * Builds the row key for a top-level organization row.
 *
 * Organization values come from different ID spaces per type (for example a Group and a Parent
 * Company can both have the value "132"), so the value alone is not unique across the rendered
 * list. Duplicate React keys make the dropdown keep stale rows when the list is filtered.
 */
function BuildOrganizationRowKey(organization) {
  return String(organization.type) + ":" + String(organization.value);
}

/**
 * Builds the row key that identifies one parent company beneath one Group.
 *
 * A parent company's value is its real RPID, and the same RPID can be reached through more than
 * one Group, so the value alone is not unique across the rendered list. The composite is a
 * render-level identity only - it is never sent to the server.
 */
function BuildChildRowKey(groupValue, parentCompanyValue) {
  return (
    String(groupValue) + DECOMPOSED_VALUE_SEPARATOR + String(parentCompanyValue)
  );
}

/**
 * Builds the parent companies that a Group's member pharmacies belong to.
 *
 * Reads the list the server nested on the Group. A Group with no qualifying parent companies
 * returns an empty list - that is a real case, and the caller still renders the Group's header.
 *
 * The generated organizations mirror the shape produced by formatOrganizationDropdownOptions in
 * Layout.js so the dropdown can render and filter them like any other row. They are display rows
 * only: selecting one records its RPID rather than replacing the selected organization.
 */
function GetParentCompaniesForGroup(group) {
  if (!IsGroup(group) || !hasValue(group)) {
    return [];
  }

  const parentCompanies = Array.isArray(group.parentCompanies)
    ? group.parentCompanies
    : [];

  // GCT labels carry no organization-type suffix, matching how Layout.formatOrganizationDropdownOptions
  // labels a non-UCT user's organizations.
  return parentCompanies.filter(hasValue).map((parentCompany) => {
    return {
      value: parentCompany.value,
      type: OrganizationTypes.ParentCompany,
      label: parentCompany.labelNoSuffix,
      labelNoSuffix: parentCompany.labelNoSuffix,
      isReconArchive: parentCompany.isReconArchive,
      isGroupContainsReconArchive: false,
      NCPDPList: parentCompany.NCPDPList,
    };
  });
}

/**
 * Returns the RPIDs of every parent company reachable through a Group, de-duplicated.
 *
 * This is the full set a Group resolves to, and therefore the default selection when the option
 * is turned on or a different Group is picked.
 */
function GetParentCompanyRpidsForGroup(group) {
  const rpids = GetParentCompaniesForGroup(group).map(
    (parentCompany) => parentCompany.value
  );

  return Array.from(new Set(rpids));
}

/**
 * Narrows a remembered RPID selection to the ones the given Group actually contains.
 *
 * A stored selection can outlive the Group it was made against - the user switches Groups, or
 * their membership changes between sessions - so anything that no longer belongs is dropped.
 *
 * Falls back to every parent company in the Group when nothing survives. A Group always resolves
 * to all of its members, so an empty refinement would mean "no data" rather than "the whole
 * Group", which is never what the user asked for.
 */
function ReconcileSelectedRpids(group, selectedRpids) {
  const availableRpids = GetParentCompanyRpidsForGroup(group);

  if (availableRpids.length < 1) {
    return [];
  }

  const requestedRpids = Array.isArray(selectedRpids) ? selectedRpids : [];
  const surviving = availableRpids.filter((rpid) =>
    requestedRpids.includes(rpid)
  );

  return surviving.length > 0 ? surviving : availableRpids;
}

/**
 * Builds the dropdown's render model while the option is on.
 *
 * Returns a flat, ordered list of rows rather than a nested structure so the dropdown can keep
 * rendering a single <ul>. Each Group becomes a header row followed by its parent companies at
 * depth 1; any other organization is an ordinary row at depth 0. The incoming order is preserved,
 * so the existing alphabetical sort still applies.
 *
 * A Group with no parent companies still gets its header row, so the user can see it exists.
 */
function BuildDecomposedOptionRows(organizations) {
  const rows = [];

  ToOrganizationList(organizations).forEach((organization) => {
    if (!IsGroup(organization)) {
      rows.push({
        rowKey: BuildOrganizationRowKey(organization),
        organization: organization,
        isGroupHeader: false,
        parentGroupValue: null,
        depth: 0,
      });

      return;
    }

    rows.push({
      rowKey: BuildOrganizationRowKey(organization),
      organization: organization,
      isGroupHeader: true,
      parentGroupValue: null,
      depth: 0,
    });

    GetParentCompaniesForGroup(organization).forEach((parentCompany) => {
      rows.push({
        rowKey: BuildChildRowKey(organization.value, parentCompany.value),
        organization: parentCompany,
        isGroupHeader: false,
        parentGroupValue: organization.value,
        depth: 1,
      });
    });
  });

  return rows;
}

/**
 * Builds the flat render model used when the option is off, which is every user other than a GCT
 * user who has turned it on.
 *
 * Produces the same row shape as BuildDecomposedOptionRows so the dropdown only has one
 * rendering path to maintain.
 */
function BuildFlatOptionRows(organizations) {
  return ToOrganizationList(organizations).map((organization) => {
    return {
      rowKey: BuildOrganizationRowKey(organization),
      organization: organization,
      isGroupHeader: false,
      parentGroupValue: null,
      depth: 0,
    };
  });
}

export {
  DECOMPOSED_VALUE_SEPARATOR,
  IsGroup,
  BuildChildRowKey,
  BuildOrganizationRowKey,
  GetParentCompaniesForGroup,
  GetParentCompanyRpidsForGroup,
  ReconcileSelectedRpids,
  BuildDecomposedOptionRows,
  BuildFlatOptionRows,
};
